import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentAPI, questionsAPI, attachmentsAPI, bookmarksAPI, bulkActionsAPI, commentsAPI, evidenceAPI, changelogAPI, questionCmsAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield,
  ArrowLeft,
  Check,
  X,
  Minus,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Flag,
  Loader2,
  FileText,
  BarChart3,
  ExternalLink,
  Paperclip,
  Upload,
  Trash2,
  ClipboardList,
  ClipboardCheck,
  Sparkles,
  CheckCircle,
  Award,
  TrendingUp,
  AlertTriangle,
  Bookmark,
  BookmarkCheck,
  MoreVertical,
  Copy,
  Save,
  FolderOpen,
  Link as LinkIcon,
  Plus,
  Lightbulb,
  Search,
  FolderSearch,
  History,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Filter,
  Calendar
} from 'lucide-react';
import VulcanBadge from '../components/VulcanBadge';
import Confetti from '../components/Confetti';
import DomainNavigator from '../components/DomainNavigator';
import DomainHeatmap from '../components/DomainHeatmap';
import DocumentAutoFill from '../components/DocumentAutoFill';

const Assessment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [response, setResponse] = useState(''); // New response field
  const [filter, setFilter] = useState('all'); // all, unanswered, flagged, bookmarked
  const [selectedDomain, setSelectedDomain] = useState(null); // Domain filter
  const [searchQuery, setSearchQuery] = useState(''); // Search controls
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [sectionComments, setSectionComments] = useState([]);
  const [showSectionComment, setShowSectionComment] = useState(false);
  const [newSectionComment, setNewSectionComment] = useState('');
  const [questionEvidence, setQuestionEvidence] = useState([]);
  const [evidenceLibrary, setEvidenceLibrary] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceCategories, setEvidenceCategories] = useState({});
  const [objectiveStatuses, setObjectiveStatuses] = useState({}); // Track sub-objective statuses for CMMC
  
  // Changelog state (admin only)
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogFilters, setChangelogFilters] = useState({
    startDate: '',
    endDate: '',
    userFilter: '',
    changeType: ''
  });
  const [availableFilters, setAvailableFilters] = useState({ users: [], changeTypes: [] });
  const [showChangelogFilters, setShowChangelogFilters] = useState(false);
  const isAdmin = user?.isAdmin || user?.role === 'admin';
  const [showObjectives, setShowObjectives] = useState(true); // Toggle objectives visibility
  const evidenceFileInputRef = useRef(null);
  const [referenceDocuments, setReferenceDocuments] = useState([]);

  useEffect(() => {
    fetchAssessment();
  }, [id]);

  useEffect(() => {
    // Fetch attachments and evidence when question changes
    if (filteredQuestions[currentIndex]) {
      fetchAttachments(filteredQuestions[currentIndex].questionId);
      fetchQuestionEvidence(filteredQuestions[currentIndex].questionId);
      fetchReferenceDocuments(filteredQuestions[currentIndex].questionId);
      // Load the response for the current question
      setResponse(filteredQuestions[currentIndex].response || '');
      setComment(filteredQuestions[currentIndex].comment || '');
      // Load objective statuses for CMMC questions
      const objectives = filteredQuestions[currentIndex].assessmentObjectives || [];
      const initialStatuses = {};
      objectives.forEach(obj => {
        initialStatuses[obj.id] = obj.status || 'U';
      });
      setObjectiveStatuses(initialStatuses);
    }
  }, [currentIndex, questions, assessment, searchQuery, selectedDomain, filter]);

  // Fetch bookmarks and comments
  useEffect(() => {
    if (id) {
      fetchBookmarks();
      fetchComments();
      fetchEvidenceCategories();
    }
  }, [id]);

  const fetchBookmarks = async () => {
    try {
      const res = await bookmarksAPI.getAll(id);
      setBookmarkedQuestions(res.data.bookmarks || []);
    } catch (err) {
      console.error('Failed to fetch bookmarks:', err);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await commentsAPI.getAll(id);
      setSectionComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const fetchEvidenceCategories = async () => {
    try {
      const res = await evidenceAPI.getCategories();
      setEvidenceCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch evidence categories:', err);
    }
  };

  const fetchQuestionEvidence = async (questionId) => {
    try {
      const res = await evidenceAPI.getForQuestion(id, questionId);
      setQuestionEvidence(res.data || []);
    } catch (err) {
      console.error('Failed to fetch question evidence:', err);
    }
  };

  const fetchEvidenceLibrary = async () => {
    try {
      const res = await evidenceAPI.getLibrary({});
      setEvidenceLibrary(res.data.evidence || []);
    } catch (err) {
      console.error('Failed to fetch evidence library:', err);
    }
  };

  const fetchReferenceDocuments = async (questionId) => {
    if (!assessment?.assessmentType) return;
    try {
      const res = await questionCmsAPI.getPublicEvidence(assessment.assessmentType, questionId);
      setReferenceDocuments(res.data.evidence || []);
    } catch {
      setReferenceDocuments([]);
    }
  };

  const handleDownloadReferenceDoc = async (doc) => {
    try {
      const res = await questionCmsAPI.downloadPublicEvidence(assessment.assessmentType, doc.questionId, doc.id);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Fetch changelog (admin only)
  const fetchChangelog = async () => {
    if (!isAdmin) return;
    
    setChangelogLoading(true);
    try {
      const res = await changelogAPI.get(id, {
        limit: 100,
        startDate: changelogFilters.startDate,
        endDate: changelogFilters.endDate,
        userFilter: changelogFilters.userFilter,
        changeType: changelogFilters.changeType
      });
      setChangelog(res.data.entries || []);
      setAvailableFilters(res.data.filters || { users: [], changeTypes: [] });
    } catch (err) {
      console.error('Failed to fetch changelog:', err);
    } finally {
      setChangelogLoading(false);
    }
  };

  // Export changelog handlers
  const handleExportCSV = () => {
    const token = localStorage.getItem('token');
    const url = changelogAPI.exportCSV(id, changelogFilters);
    // Open in new tab with auth
    window.open(`${url}&token=${token}`, '_blank');
  };

  const handleExportPDF = () => {
    const token = localStorage.getItem('token');
    const url = changelogAPI.exportPDF(id, changelogFilters);
    window.open(`${url}&token=${token}`, '_blank');
  };

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const currentQ = filteredQuestions[currentIndex];
    if (!currentQ) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('category', 'other');
      formData.append('assessmentId', id);
      formData.append('questionId', currentQ.questionId);

      await evidenceAPI.upload(formData);
      notify.success('Evidence uploaded successfully');
      fetchQuestionEvidence(currentQ.questionId);
    } catch (err) {
      notify.error('Failed to upload evidence');
    } finally {
      setUploading(false);
      if (evidenceFileInputRef.current) {
        evidenceFileInputRef.current.value = '';
      }
    }
  };

  const handleLinkEvidence = async (evidenceId) => {
    const currentQ = filteredQuestions[currentIndex];
    if (!currentQ) return;

    try {
      await evidenceAPI.link({
        evidenceId,
        questionId: currentQ.questionId
      });
      notify.success('Evidence linked to question');
      fetchQuestionEvidence(currentQ.questionId);
      setShowEvidenceModal(false);
    } catch (err) {
      notify.error('Failed to link evidence');
    }
  };

  const handleUnlinkEvidence = async (evidenceId) => {
    const currentQ = filteredQuestions[currentIndex];
    if (!currentQ) return;

    try {
      await evidenceAPI.unlink(evidenceId, currentQ.questionId);
      notify.success('Evidence unlinked');
      fetchQuestionEvidence(currentQ.questionId);
    } catch (err) {
      notify.error('Failed to unlink evidence');
    }
  };

  const fetchAssessment = async () => {
    try {
      const [assessmentRes, questionsRes] = await Promise.all([
        assessmentAPI.getDetail(id),
        questionsAPI.getQuestions(id)
      ]);
      setAssessment(assessmentRes.data);
      setQuestions(questionsRes.data.questions);
    } catch (error) {
      console.error('Failed to fetch assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBookmark = async (questionId) => {
    try {
      const res = await bookmarksAPI.toggle(id, questionId);
      if (res.data.bookmarked) {
        setBookmarkedQuestions([...bookmarkedQuestions, questionId]);
        notify.info('Question bookmarked');
      } else {
        setBookmarkedQuestions(bookmarkedQuestions.filter(q => q !== questionId));
        notify.info('Bookmark removed');
      }
    } catch (err) {
      notify.error('Failed to toggle bookmark');
    }
  };

  const handleBulkAction = async (action) => {
    try {
      const res = await bulkActionsAPI.bulkAnswer(id, { action });
      notify.success(`Updated ${res.data.updatedCount} questions`);
      fetchAssessment();
      setShowBulkActions(false);
    } catch (err) {
      notify.error('Failed to perform bulk action');
    }
  };

  // Bulk update Level of Effort or Priority for filtered questions
  const handleBulkEffortPriority = async (field, value) => {
    try {
      // Get question IDs for filtered questions
      const questionIds = filteredQuestions.map(q => q.questionId);
      
      const res = await bulkActionsAPI.bulkEffortPriority(id, {
        field,
        value,
        questionIds
      });
      
      notify.success(`Updated ${field === 'levelOfEffort' ? 'Level of Effort' : 'Level of Priority'} for ${res.data.updatedCount} questions`);
      fetchAssessment();
    } catch (err) {
      console.error('Bulk update error:', err);
      notify.error('Failed to perform bulk update');
    }
  };

  const addSectionComment = async () => {
    if (!newSectionComment.trim()) return;
    const currentQ = filteredQuestions[currentIndex];
    if (!currentQ) return;

    try {
      const res = await commentsAPI.add(id, {
        section: currentQ.category,
        comment: newSectionComment
      });
      setSectionComments([...sectionComments, res.data]);
      setNewSectionComment('');
      setShowSectionComment(false);
      notify.success('Comment added');
    } catch (err) {
      notify.error('Failed to add comment');
    }
  };

  const deleteSectionComment = async (commentId) => {
    try {
      await commentsAPI.delete(id, commentId);
      setSectionComments(sectionComments.filter(c => c.id !== commentId));
      notify.success('Comment deleted');
    } catch (err) {
      notify.error('Failed to delete comment');
    }
  };

  const fetchAttachments = async (questionId) => {
    try {
      const response = await attachmentsAPI.getForQuestion(id, questionId);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const question = filteredQuestions[currentIndex];
    setUploading(true);
    try {
      await attachmentsAPI.upload(id, question.questionId, file);
      await fetchAttachments(question.questionId);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await attachmentsAPI.delete(attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAnswer = async (answerValue) => {
    const question = filteredQuestions[currentIndex];
    setSaving(true);
    
    try {
      const responsePayload = await questionsAPI.submitAnswer(id, {
        questionId: question.questionId,
        answer: answerValue,
        comment: comment,
        response: response, // Include the response field
        markForReview: question.markForReview || false,
        objectiveStatuses: objectiveStatuses, // Include CMMC sub-objective statuses
        mustStatementStatuses: question.mustStatementStatuses || {},
        evidenceChecklistStatuses: question.evidenceChecklistStatuses || {},
      });

      // Update local state with objective statuses
      const updatedQuestions = questions.map(q => 
        q.questionId === question.questionId 
          ? { 
              ...q, 
              answer: answerValue, 
              comment, 
              response,
              assessmentObjectives: (q.assessmentObjectives || []).map(obj => ({
                ...obj,
                status: objectiveStatuses[obj.id] || obj.status
              }))
            } 
          : q
      );
      setQuestions(updatedQuestions);
      setAssessment(prev => ({
        ...prev,
        answeredQuestions: responsePayload.data.answeredQuestions,
        score: responsePayload.data.score,
        status: responsePayload.data.status
      }));

      // Show success notification
      const answerLabel = answerValue === 'Y' ? 'Yes' : answerValue === 'N' ? 'No' : 'N/A';
      notify.success(`Answer saved: ${answerLabel}`);

      // Check if assessment is complete (all questions answered)
      const totalQuestions = questions.length;
      const newAnsweredCount = responsePayload.data.answeredQuestions;
      
      if (newAnsweredCount >= totalQuestions && currentIndex === filteredQuestions.length - 1) {
        // Show completion modal
        setShowCompletionModal(true);
      } else if (currentIndex < filteredQuestions.length - 1) {
        // Move to next question if not at end
        setCurrentIndex(currentIndex + 1);
        setComment('');
        setResponse('');
        setShowComment(false);
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      notify.error(error.response?.data?.detail || 'Failed to save answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleMarkForReview = async () => {
    const question = filteredQuestions[currentIndex];
    const newValue = !question.markForReview;
    
    try {
      await questionsAPI.submitAnswer(id, {
        questionId: question.questionId,
        answer: question.answer || 'U',
        comment: question.comment || '',
        markForReview: newValue
      });

      const updatedQuestions = questions.map(q => 
        q.questionId === question.questionId 
          ? { ...q, markForReview: newValue } 
          : q
      );
      setQuestions(updatedQuestions);
    } catch (error) {
      console.error('Failed to toggle flag:', error);
    }
  };

  // Handle Level of Effort and Priority changes
  const handleEffortPriorityChange = async (field, value) => {
    const question = filteredQuestions[currentIndex];
    setSaving(true);
    
    try {
      await questionsAPI.submitAnswer(id, {
        questionId: question.questionId,
        answer: question.answer || 'N',
        comment: question.comment || '',
        response: question.response || '',
        markForReview: question.markForReview || false,
        levelOfEffort: field === 'levelOfEffort' ? value : (question.levelOfEffort || ''),
        levelOfPriority: field === 'levelOfPriority' ? value : (question.levelOfPriority || '')
      });

      // Update local state
      const updatedQuestions = questions.map(q => 
        q.questionId === question.questionId 
          ? { ...q, [field]: value } 
          : q
      );
      setQuestions(updatedQuestions);
      notify(`${field === 'levelOfEffort' ? 'Level of Effort' : 'Level of Priority'} updated`, 'success');
    } catch (error) {
      console.error('Failed to update:', error);
      notify('Failed to update. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle MUST statement status toggle (DOES / DOES_NOT)
  const handleMustStatementToggle = async (statementIndex, newStatus) => {
    const question = filteredQuestions[currentIndex];
    const updatedStatuses = { ...(question.mustStatementStatuses || {}), [String(statementIndex)]: newStatus };

    const updatedQuestions = questions.map(q =>
      q.questionId === question.questionId
        ? { ...q, mustStatementStatuses: updatedStatuses }
        : q
    );
    setQuestions(updatedQuestions);

    try {
      await questionsAPI.submitAnswer(id, {
        questionId: question.questionId,
        answer: question.answer || 'N',
        comment: question.comment || '',
        response: question.response || '',
        markForReview: question.markForReview || false,
        mustStatementStatuses: updatedStatuses,
        evidenceChecklistStatuses: question.evidenceChecklistStatuses || {},
      });
    } catch (error) {
      console.error('Failed to save MUST statement status:', error);
    }
  };

  // Handle evidence checklist toggle
  const handleEvidenceChecklistToggle = async (evidenceIndex) => {
    const question = filteredQuestions[currentIndex];
    const currentStatuses = question.evidenceChecklistStatuses || {};
    const updatedStatuses = { ...currentStatuses, [String(evidenceIndex)]: !currentStatuses[String(evidenceIndex)] };

    const updatedQuestions = questions.map(q =>
      q.questionId === question.questionId
        ? { ...q, evidenceChecklistStatuses: updatedStatuses }
        : q
    );
    setQuestions(updatedQuestions);

    try {
      await questionsAPI.submitAnswer(id, {
        questionId: question.questionId,
        answer: question.answer || 'N',
        comment: question.comment || '',
        response: question.response || '',
        markForReview: question.markForReview || false,
        mustStatementStatuses: question.mustStatementStatuses || {},
        evidenceChecklistStatuses: updatedStatuses,
      });
    } catch (error) {
      console.error('Failed to save evidence checklist:', error);
    }
  };

  const filteredQuestions = questions.filter(q => {
    // Apply search filter first
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesId = q.questionId?.toLowerCase().includes(query);
      const matchesText = q.questionText?.toLowerCase().includes(query);
      const matchesCategory = q.category?.toLowerCase().includes(query);
      const matchesDomain = q.domain?.toLowerCase().includes(query);
      const matchesSubCategory = q.subCategory?.toLowerCase().includes(query);
      if (!matchesId && !matchesText && !matchesCategory && !matchesDomain && !matchesSubCategory) {
        return false;
      }
    }
    
    // Apply domain filter
    if (selectedDomain) {
      const qDomain = q.category || q.domain || 'Uncategorized';
      if (qDomain !== selectedDomain) return false;
    }
    // Then apply answer/flag filters
    // Note: Default answer is now 'No' - questions need to earn 'Yes'
    if (filter === 'unanswered') return q.answer === 'N' || q.answer === 'U';  // Show No/Unanswered as "needs review"
    if (filter === 'flagged') return q.markForReview;
    if (filter === 'bookmarked') return bookmarkedQuestions.includes(q.questionId);
    return true;
  });

  // Keyboard navigation: 1=Yes, 2=No, 3=N/A, ←/→=Navigate questions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (!filteredQuestions.length) return;
      
      switch (e.key) {
        case '1':
          e.preventDefault();
          handleAnswer('Y');
          break;
        case '2':
          e.preventDefault();
          handleAnswer('N');
          break;
        case '3':
          e.preventDefault();
          handleAnswer('NA');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setComment('');
            setResponse('');
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentIndex < filteredQuestions.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setComment('');
            setResponse('');
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, filteredQuestions.length]);

  // Auto-save response/comment after 2 seconds of inactivity
  const autoSaveTimerRef = useRef(null);
  
  useEffect(() => {
    if (!filteredQuestions.length) return;
    const question = filteredQuestions[currentIndex];
    if (!question) return;
    
    // Check if response or comment has changed from saved value
    const hasChanges = (response !== (question.response || '')) || 
                       (comment !== (question.comment || ''));
    
    if (hasChanges && (response || comment)) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // Set new timer for auto-save (2 seconds)
      autoSaveTimerRef.current = setTimeout(async () => {
        try {
          await questionsAPI.submitAnswer(id, {
            questionId: question.questionId,
            answer: question.answer || 'U',
            comment: comment,
            response: response,
            markForReview: question.markForReview || false
          });
          
          // Update local state
          const updatedQuestions = questions.map(q => 
            q.questionId === question.questionId 
              ? { ...q, comment, response } 
              : q
          );
          setQuestions(updatedQuestions);
          
          notify('Draft saved', 'info');
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }, 2000);
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [response, comment, currentIndex, filteredQuestions.length]);

  // Handler for selecting a question from domain navigator
  const handleSelectQuestion = (questionId) => {
    const idx = filteredQuestions.findIndex(q => q.questionId === questionId);
    if (idx !== -1) {
      setCurrentIndex(idx);
    } else {
      // Question not in current filter - clear filter and find in all questions
      setFilter('all');
      setSelectedDomain(null);
      const allIdx = questions.findIndex(q => q.questionId === questionId);
      if (allIdx !== -1) {
        setTimeout(() => setCurrentIndex(allIdx), 100);
      }
    }
  };

  const currentQuestion = filteredQuestions[currentIndex];
  const isBookmarked = currentQuestion && bookmarkedQuestions.includes(currentQuestion.questionId);
  const currentSectionComments = sectionComments.filter(c => c.section === currentQuestion?.category);

  const getAnswerStats = () => {
    const stats = { Y: 0, N: 0, NA: 0, U: 0 };
    questions.forEach(q => {
      // Default is now 'N' (No), not 'U' (Unanswered)
      stats[q.answer || 'N']++;
    });
    return stats;
  };

  const stats = getAnswerStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">No questions match your filter</h2>
          <button
            onClick={() => setFilter('all')}
            className="px-6 py-3 bg-violet-600 text-white rounded-lg"
          >
            Show All Questions
          </button>
        </div>
      </div>
    );
  }

  // Calculate completion stats for modal
  const answeredCount = questions.filter(q => q.answer && q.answer !== 'U').length;
  const yesCount = questions.filter(q => q.answer === 'Y').length;
  const noCount = questions.filter(q => q.answer === 'N').length;
  const naCount = questions.filter(q => q.answer === 'NA').length;
  const applicableCount = yesCount + noCount;
  const complianceScore = applicableCount > 0 ? Math.round((yesCount / applicableCount) * 100) : 0;

  return (
    <div data-testid="assessment-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950/30 to-slate-900">
      {/* Confetti for 100% Compliance */}
      {showCompletionModal && complianceScore === 100 && <Confetti active={true} duration={5000} />}
      
      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className={`bg-gradient-to-br ${
            complianceScore === 100 
              ? 'from-violet-900 via-slate-800 to-violet-900 border-violet-500/50' 
              : 'from-slate-800 to-slate-900 border-violet-500/30'
          } rounded-2xl border p-8 max-w-lg w-full mx-4 text-center`}>
            
            {/* Vulcan Badge for 100% Compliance */}
            {complianceScore === 100 ? (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
                    <h2 className="text-3xl font-bold text-white">VULCAN LEVEL ACHIEVED!</h2>
                    <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
                  </div>
                  <p className="text-violet-200">
                    Perfect compliance! You&apos;ve earned the highest honor. Live Long &amp; Stay Secure.
                  </p>
                </div>
                
                <VulcanBadge 
                  assessmentType={assessment?.assessmentType}
                  assessmentName={assessment?.name}
                  size="medium"
                />
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-3xl font-bold text-white mb-2">Assessment Complete!</h2>
                <p className="text-slate-400 mb-6">
                  Congratulations! You have answered all {questions.length} questions.
                </p>
              </>
            )}
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6 mt-6">
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-green-400">{yesCount}</div>
                <div className="text-xs text-slate-400">Compliant</div>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-red-400">{noCount}</div>
                <div className="text-xs text-slate-400">Non-Compliant</div>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-slate-400">{naCount}</div>
                <div className="text-xs text-slate-400">N/A</div>
              </div>
            </div>
            
            {/* Compliance Score */}
            {complianceScore !== 100 && (
              <div className="bg-slate-700/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-3">
                  <TrendingUp className={`w-6 h-6 ${complianceScore >= 75 ? 'text-green-400' : complianceScore > 50 ? 'text-yellow-400' : 'text-red-400'}`} />
                  <span className="text-slate-300">Compliance Score:</span>
                  <span className={`text-3xl font-bold ${complianceScore >= 75 ? 'text-green-400' : complianceScore > 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {complianceScore}%
                  </span>
                </div>
              </div>
            )}
            
            {noCount > 0 && (
              <div className="flex items-center gap-2 justify-center text-yellow-400 mb-6">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{noCount} finding(s) require attention</span>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/report/${id}`)}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
              >
                <BarChart3 className="w-5 h-5" />
                View Full Report
              </button>
              <button
                onClick={() => navigate(`/findings/${id}`)}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-all"
              >
                <AlertTriangle className="w-5 h-5" />
                View Findings & Remediation
              </button>
              <button
                onClick={() => setShowCompletionModal(false)}
                className="w-full px-6 py-3 text-slate-400 hover:text-white transition-colors"
              >
                Continue Reviewing Questions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-800/50 border-b border-violet-500/20 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                <div>
                  <h1 className="text-xl font-bold text-white">{assessment?.name}</h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      assessment?.assessmentType === 'CMMC'
                        ? 'bg-blue-500/20 text-blue-400'
                        : assessment?.assessmentType === 'NIST_CSF'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {assessment?.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment?.assessmentType}
                    </span>
                    <span className="text-slate-400 text-sm">
                      Question {currentIndex + 1} of {filteredQuestions.length}
                      {selectedDomain && (
                        <span className="ml-2 px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded text-xs">
                          {selectedDomain}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-white">{assessment?.score}%</div>
                <div className="text-sm text-slate-400">Score</div>
              </div>
              <button
                data-testid="view-readiness-btn"
                onClick={() => navigate(`/assessment/${id}/readiness`)}
                className="flex items-center gap-2 px-4 py-2 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors"
              >
                <Shield className="w-4 h-4" />
                Readiness
              </button>
              {assessment?.assessmentType === 'NIST_CSF' && (
                <button
                  data-testid="view-ssp-btn"
                  onClick={() => navigate(`/assessment/${id}/ssp/nist-csf`)}
                  className="flex items-center gap-2 px-4 py-2 border border-violet-500/50 text-violet-300 hover:bg-violet-500/10 rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  View SSP
                </button>
              )}
              <button
                data-testid="view-findings-btn"
                onClick={() => navigate(`/findings/${id}`)}
                className="flex items-center gap-2 px-4 py-2 border border-violet-500/50 text-violet-300 hover:bg-violet-500/10 rounded-lg transition-colors"
              >
                <ClipboardList className="w-4 h-4" />
                Findings
              </button>
              <button
                data-testid="view-report-btn"
                onClick={() => navigate(`/report/${id}`)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Report
              </button>
              {/* Show Complete button when all questions are answered */}
              {answeredCount >= questions.length && questions.length > 0 && (
                <button
                  onClick={() => setShowCompletionModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg transition-colors animate-pulse"
                >
                  <CheckCircle className="w-4 h-4" />
                  Complete
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        className="hidden"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar - Full Width */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              data-testid="assessment-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentIndex(0); }}
              placeholder="Search controls by ID, text, category, or domain..."
              className="w-full pl-12 pr-40 py-3 bg-slate-800/70 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/30 transition-colors"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {searchQuery && (
                <>
                  <span className="text-xs text-slate-400 tabular-nums">
                    {filteredQuestions.length} result{filteredQuestions.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    data-testid="assessment-search-clear"
                    onClick={() => { setSearchQuery(''); setCurrentIndex(0); }}
                    className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Domain Heatmap - Full Width */}
        <div className="mb-6">
          <DomainHeatmap
            questions={questions}
            selectedDomain={selectedDomain}
            onSelectDomain={(domain) => { setSelectedDomain(domain); setCurrentIndex(0); }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Progress & Stats */}
          <div className="lg:col-span-1 space-y-4">
            {/* Progress Card */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-violet-500/20">
              <h3 className="text-lg font-semibold text-white mb-4">Progress</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Answered</span>
                  <span className="text-white font-semibold">
                    {assessment?.answeredQuestions}/{assessment?.totalQuestions}
                  </span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-violet-500 rounded-full transition-all"
                    style={{ width: `${(assessment?.answeredQuestions / assessment?.totalQuestions) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Document Auto-Fill */}
            <DocumentAutoFill 
              assessmentId={id}
              assessmentType={assessment?.assessmentType}
              onComplete={fetchAssessment}
            />

            {/* Stats Card */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Answers</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                    <span className="text-slate-400">Yes</span>
                  </div>
                  <span className="text-white font-semibold">{stats.Y}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <span className="text-slate-400">No</span>
                  </div>
                  <span className="text-white font-semibold">{stats.N}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full" />
                    <span className="text-slate-400">N/A</span>
                  </div>
                  <span className="text-white font-semibold">{stats.NA}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-slate-600 rounded-full" />
                    <span className="text-slate-400">Unanswered</span>
                  </div>
                  <span className="text-white font-semibold">{stats.U}</span>
                </div>
              </div>
            </div>

            {/* Domain Navigator */}
            <DomainNavigator
              questions={questions}
              currentQuestionId={currentQuestion?.questionId}
              selectedDomain={selectedDomain}
              onSelectDomain={(domain) => { setSelectedDomain(domain); setCurrentIndex(0); }}
              onSelectQuestion={handleSelectQuestion}
            />

            {/* Filter */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Filter</h3>
              <div className="space-y-2">
                {['all', 'unanswered', 'flagged', 'bookmarked'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setCurrentIndex(0); }}
                    className={`w-full px-4 py-2 rounded-lg text-left capitalize transition-colors flex items-center gap-2 ${
                      filter === f
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                        : 'text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    {f === 'bookmarked' && <BookmarkCheck className="w-4 h-4" />}
                    {f === 'all' ? 'All Questions' : f}
                    {f === 'bookmarked' && ` (${bookmarkedQuestions.length})`}
                  </button>
                ))}
              </div>
            </div>

            {/* Bulk Actions */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Bulk Actions</h3>
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="p-1 text-slate-400 hover:text-white"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
              {showBulkActions && (
                <div className="space-y-3">
                  {/* Answer Bulk Actions */}
                  <div className="pb-3 border-b border-slate-700">
                    <p className="text-xs text-slate-500 uppercase mb-2">Answer Actions</p>
                    <div className="space-y-1">
                      <button
                        onClick={() => handleBulkAction('markNA')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        Mark all unanswered as N/A
                      </button>
                      <button
                        onClick={() => handleBulkAction('markYes')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        Mark all unanswered as Yes
                      </button>
                      <button
                        onClick={() => handleBulkAction('markNo')}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        Mark all unanswered as No
                      </button>
                    </div>
                  </div>
                  
                  {/* Bulk Effort Assignment */}
                  <div className="pb-3 border-b border-slate-700">
                    <p className="text-xs text-slate-500 uppercase mb-2">Bulk Level of Effort</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfEffort', 'Low')}
                        className="flex-1 px-3 py-2 text-sm bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg transition-colors border border-emerald-500/30"
                        data-testid="bulk-effort-low"
                      >
                        Low
                      </button>
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfEffort', 'Medium')}
                        className="flex-1 px-3 py-2 text-sm bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition-colors border border-amber-500/30"
                        data-testid="bulk-effort-medium"
                      >
                        Medium
                      </button>
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfEffort', 'High')}
                        className="flex-1 px-3 py-2 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors border border-red-500/30"
                        data-testid="bulk-effort-high"
                      >
                        High
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Apply to all {filter !== 'all' ? filter : ''} questions{selectedDomain ? ` in ${selectedDomain}` : ''}</p>
                  </div>
                  
                  {/* Bulk Priority Assignment */}
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-2">Bulk Level of Priority</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfPriority', 'Low')}
                        className="flex-1 px-3 py-2 text-sm bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg transition-colors border border-emerald-500/30"
                        data-testid="bulk-priority-low"
                      >
                        Low
                      </button>
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfPriority', 'Medium')}
                        className="flex-1 px-3 py-2 text-sm bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition-colors border border-amber-500/30"
                        data-testid="bulk-priority-medium"
                      >
                        Medium
                      </button>
                      <button
                        onClick={() => handleBulkEffortPriority('levelOfPriority', 'High')}
                        className="flex-1 px-3 py-2 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors border border-red-500/30"
                        data-testid="bulk-priority-high"
                      >
                        High
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Apply to all {filter !== 'all' ? filter : ''} questions{selectedDomain ? ` in ${selectedDomain}` : ''}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Section Comments */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Section Notes</h3>
                <button
                  onClick={() => setShowSectionComment(!showSectionComment)}
                  className="p-1 text-slate-400 hover:text-violet-400"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
              </div>
              {showSectionComment && (
                <div className="mb-4">
                  <textarea
                    value={newSectionComment}
                    onChange={(e) => setNewSectionComment(e.target.value)}
                    placeholder="Add a note for this section..."
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm resize-none"
                    rows={2}
                  />
                  <button
                    onClick={addSectionComment}
                    className="mt-2 px-3 py-1 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700"
                  >
                    Add Note
                  </button>
                </div>
              )}
              {currentSectionComments.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {currentSectionComments.map((c) => (
                    <div key={c.id} className="p-2 bg-slate-700/50 rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-slate-300">{c.comment}</p>
                        <button
                          onClick={() => deleteSectionComment(c.id)}
                          className="text-slate-500 hover:text-red-400 flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{c.userName}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No notes for this section</p>
              )}
            </div>
            
            {/* Change Log (Admin Only) */}
            {isAdmin && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-violet-400" />
                    <h3 className="text-lg font-semibold text-white">Change Log</h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowChangelog(!showChangelog);
                      if (!showChangelog && changelog.length === 0) {
                        fetchChangelog();
                      }
                    }}
                    className="p-1 text-slate-400 hover:text-violet-400"
                    data-testid="changelog-toggle"
                  >
                    {showChangelog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                
                {showChangelog && (
                  <div className="space-y-3">
                    {/* Filter Toggle and Export Buttons */}
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => setShowChangelogFilters(!showChangelogFilters)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          showChangelogFilters || changelogFilters.startDate || changelogFilters.endDate || changelogFilters.userFilter || changelogFilters.changeType
                            ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                            : 'text-slate-400 hover:bg-slate-700 border border-slate-600'
                        }`}
                        data-testid="changelog-filter-toggle"
                      >
                        <Filter className="w-3.5 h-3.5" />
                        Filters
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handleExportCSV}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors"
                          title="Export to CSV"
                          data-testid="export-csv-btn"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                          CSV
                        </button>
                        <button
                          onClick={handleExportPDF}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Export to PDF"
                          data-testid="export-pdf-btn"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          PDF
                        </button>
                      </div>
                    </div>
                    
                    {/* Filters Panel */}
                    {showChangelogFilters && (
                      <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600/50 space-y-3">
                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Start Date</label>
                            <input
                              type="date"
                              value={changelogFilters.startDate}
                              onChange={(e) => setChangelogFilters(prev => ({ ...prev, startDate: e.target.value }))}
                              className="w-full px-2 py-1.5 text-sm bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                              data-testid="changelog-start-date"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">End Date</label>
                            <input
                              type="date"
                              value={changelogFilters.endDate}
                              onChange={(e) => setChangelogFilters(prev => ({ ...prev, endDate: e.target.value }))}
                              className="w-full px-2 py-1.5 text-sm bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                              data-testid="changelog-end-date"
                            />
                          </div>
                        </div>
                        
                        {/* User Filter */}
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">User</label>
                          <select
                            value={changelogFilters.userFilter}
                            onChange={(e) => setChangelogFilters(prev => ({ ...prev, userFilter: e.target.value }))}
                            className="w-full px-2 py-1.5 text-sm bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                            data-testid="changelog-user-filter"
                          >
                            <option value="">All Users</option>
                            {availableFilters.users.map(email => (
                              <option key={email} value={email}>{email}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Change Type Filter */}
                        <div>
                          <label className="text-xs text-slate-400 block mb-1">Change Type</label>
                          <select
                            value={changelogFilters.changeType}
                            onChange={(e) => setChangelogFilters(prev => ({ ...prev, changeType: e.target.value }))}
                            className="w-full px-2 py-1.5 text-sm bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-violet-500"
                            data-testid="changelog-type-filter"
                          >
                            <option value="">All Types</option>
                            {availableFilters.changeTypes.map(type => (
                              <option key={type} value={type}>{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Apply/Clear Buttons */}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={fetchChangelog}
                            className="flex-1 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
                            data-testid="apply-filters-btn"
                          >
                            Apply Filters
                          </button>
                          <button
                            onClick={() => {
                              setChangelogFilters({ startDate: '', endDate: '', userFilter: '', changeType: '' });
                              setTimeout(fetchChangelog, 100);
                            }}
                            className="px-3 py-1.5 text-sm text-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                            data-testid="clear-filters-btn"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Changelog Entries */}
                    {changelogLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                      </div>
                    ) : changelog.length > 0 ? (
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {changelog.map((entry, idx) => (
                          <div key={entry.id || idx} className="p-3 bg-slate-700/50 rounded-lg border border-slate-600/50">
                            <div className="flex items-start gap-3">
                              <div className={`p-1.5 rounded-lg ${
                                entry.changeType === 'answer_updated' ? 'bg-emerald-500/20 text-emerald-400' :
                                entry.changeType === 'bulk_answer' ? 'bg-amber-500/20 text-amber-400' :
                                entry.changeType === 'bulk_effort_priority' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-600 text-slate-300'
                              }`}>
                                {entry.changeType === 'answer_updated' ? <Check className="w-3 h-3" /> :
                                 entry.changeType.includes('bulk') ? <ClipboardList className="w-3 h-3" /> :
                                 <History className="w-3 h-3" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white font-medium truncate">{entry.description}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <User className="w-3 h-3 text-slate-500" />
                                  <span className="text-xs text-slate-400">{entry.userName || entry.userEmail}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Clock className="w-3 h-3 text-slate-500" />
                                  <span className="text-xs text-slate-500">
                                    {new Date(entry.timestamp).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-2">No changes recorded yet</p>
                    )}
                    
                    {changelog.length > 0 && (
                      <button
                        onClick={fetchChangelog}
                        className="w-full px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <History className="w-4 h-4" />
                        Refresh Log
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Content - Question */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
              {/* Question Header */}
              <div className="bg-slate-800 px-6 py-4 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                      assessment?.assessmentType === 'CMMC'
                        ? 'bg-blue-500/20 text-blue-400'
                        : assessment?.assessmentType === 'NIST_CSF'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {currentQuestion.category}
                    </span>
                    <span className="text-slate-500 text-sm">
                      {currentQuestion.domain || currentQuestion.subCategory} {currentQuestion.level ? `| Level ${currentQuestion.level}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleBookmark(currentQuestion.questionId)}
                      className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
                        isBookmarked
                          ? 'bg-violet-500/20 text-violet-400'
                          : 'text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                      {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                    </button>
                    <button
                      onClick={toggleMarkForReview}
                      className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
                        currentQuestion.markForReview
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      <Flag className="w-4 h-4" />
                      {currentQuestion.markForReview ? 'Flagged' : 'Flag'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-6">
                <div className="mb-2 flex items-center gap-3">
                  <span className="text-sm text-slate-500 font-mono">{currentQuestion.questionId}</span>
                  {currentQuestion.priorityNumber && (
                    <span 
                      data-testid="priority-badge"
                      className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                        currentQuestion.priorityNumber <= 20 ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                        currentQuestion.priorityNumber <= 50 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                        'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                      }`}
                    >
                      Priority #{currentQuestion.priorityNumber}
                    </span>
                  )}
                </div>
                <h2 className="text-xl text-white leading-relaxed mb-6">
                  {currentQuestion.questionText}
                </h2>

                {/* CMMC Assessment Objectives (Sub-Controls) */}
                {currentQuestion.assessmentObjectives && currentQuestion.assessmentObjectives.length > 0 && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-blue-400 text-sm">
                        <ClipboardList className="w-4 h-4" />
                        <span className="font-semibold">Assessment Objectives</span>
                        <span className="text-blue-300/60">({currentQuestion.assessmentObjectives.length} sub-requirements)</span>
                      </div>
                      <button
                        onClick={() => setShowObjectives(!showObjectives)}
                        className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 bg-blue-500/10 rounded"
                      >
                        {showObjectives ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {showObjectives && (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-400 mb-3">
                          For this control to be MET, all objectives must be satisfied. Click each objective to mark its status.
                        </p>
                        {currentQuestion.assessmentObjectives.map((obj) => (
                          <div 
                            key={obj.id} 
                            className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                              objectiveStatuses[obj.id] === 'MET' 
                                ? 'bg-green-500/10 border border-green-500/30' 
                                : objectiveStatuses[obj.id] === 'NOT_MET'
                                ? 'bg-red-500/10 border border-red-500/30'
                                : objectiveStatuses[obj.id] === 'NA'
                                ? 'bg-slate-500/10 border border-slate-500/30'
                                : 'bg-slate-700/30 border border-slate-600/30'
                            }`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold">
                                {obj.id}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-200">{obj.text}</p>
                            </div>
                            <div className="flex-shrink-0 flex gap-1">
                              <button
                                onClick={() => setObjectiveStatuses(prev => ({ ...prev, [obj.id]: 'MET' }))}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  objectiveStatuses[obj.id] === 'MET'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-slate-600 text-slate-300 hover:bg-green-500/50'
                                }`}
                                title="MET"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => setObjectiveStatuses(prev => ({ ...prev, [obj.id]: 'NOT_MET' }))}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  objectiveStatuses[obj.id] === 'NOT_MET'
                                    ? 'bg-red-500 text-white'
                                    : 'bg-slate-600 text-slate-300 hover:bg-red-500/50'
                                }`}
                                title="NOT MET"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => setObjectiveStatuses(prev => ({ ...prev, [obj.id]: 'NA' }))}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  objectiveStatuses[obj.id] === 'NA'
                                    ? 'bg-slate-500 text-white'
                                    : 'bg-slate-600 text-slate-300 hover:bg-slate-500/50'
                                }`}
                                title="N/A"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Summary of objective status */}
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-600/50">
                          <span className="text-xs text-slate-400">Status:</span>
                          <div className="flex gap-3 text-xs">
                            <span className="flex items-center gap-1 text-green-400">
                              <Check className="w-3 h-3" />
                              {Object.values(objectiveStatuses).filter(s => s === 'MET').length} MET
                            </span>
                            <span className="flex items-center gap-1 text-red-400">
                              <X className="w-3 h-3" />
                              {Object.values(objectiveStatuses).filter(s => s === 'NOT_MET').length} NOT MET
                            </span>
                            <span className="flex items-center gap-1 text-slate-400">
                              <Minus className="w-3 h-3" />
                              {Object.values(objectiveStatuses).filter(s => s === 'NA').length} N/A
                            </span>
                            <span className="flex items-center gap-1 text-blue-400">
                              {Object.values(objectiveStatuses).filter(s => s === 'U' || !s).length} Pending
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Plain Language Explanation (for CMMC) */}
                {currentQuestion.plainLanguage && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
                      <HelpCircle className="w-4 h-4" />
                      <span className="font-semibold">What This Means (Plain Language)</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">{currentQuestion.plainLanguage}</p>
                  </div>
                )}

                {/* MUST Statements - Interactive DOES / DOES NOT toggle */}
                {currentQuestion.mustStatements && currentQuestion.mustStatements.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-amber-400 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-semibold">Compliance Requirements (MUST)</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {Object.values(currentQuestion.mustStatementStatuses || {}).filter(s => s === 'DOES').length}/{currentQuestion.mustStatements.length} met
                      </span>
                    </div>
                    <div className="space-y-3">
                      {currentQuestion.mustStatements.map((statement, idx) => {
                        const status = (currentQuestion.mustStatementStatuses || {})[String(idx)] || '';
                        return (
                          <div key={idx} className={`rounded-lg p-3 transition-colors ${
                            status === 'DOES' ? 'bg-emerald-500/10 border border-emerald-500/20' :
                            status === 'DOES_NOT' ? 'bg-red-500/10 border border-red-500/20' :
                            'bg-slate-800/50 border border-slate-700'
                          }`}>
                            <p className="text-sm text-slate-300 mb-2.5 leading-relaxed">{statement}</p>
                            <div className="flex items-center gap-2">
                              <button
                                data-testid={`must-does-${idx}`}
                                onClick={() => handleMustStatementToggle(idx, status === 'DOES' ? '' : 'DOES')}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${
                                  status === 'DOES'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-emerald-500/20 hover:text-emerald-400'
                                }`}
                              >
                                DOES
                              </button>
                              <button
                                data-testid={`must-does-not-${idx}`}
                                onClick={() => handleMustStatementToggle(idx, status === 'DOES_NOT' ? '' : 'DOES_NOT')}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${
                                  status === 'DOES_NOT'
                                    ? 'bg-red-500 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-red-500/20 hover:text-red-400'
                                }`}
                              >
                                DOES NOT
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Example Scenarios (for CMMC) */}
                {currentQuestion.exampleScenarios && currentQuestion.exampleScenarios.length > 0 && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-purple-400 text-sm mb-3">
                      <Lightbulb className="w-4 h-4" />
                      <span className="font-semibold">Real-World Examples</span>
                    </div>
                    <div className="space-y-3">
                      {currentQuestion.exampleScenarios.map((scenario, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-sm text-slate-300 leading-relaxed">{scenario}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Guidance (for CMMC) */}
                {currentQuestion.evidenceGuidance && currentQuestion.evidenceGuidance.length > 0 && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-cyan-400 text-sm mb-3">
                      <FolderSearch className="w-4 h-4" />
                      <span className="font-semibold">Evidence to Collect</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">
                      Assessors will look for the following documentation:
                    </p>
                    <ul className="space-y-2">
                      {currentQuestion.evidenceGuidance.map((evidence, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-cyan-400 mt-1 flex-shrink-0">✓</span>
                          <span>{evidence}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Control Cross-References (for CMMC) */}
                {currentQuestion.relatedRequirements && currentQuestion.relatedRequirements.length > 0 && (
                  <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-indigo-400 text-sm mb-3">
                      <LinkIcon className="w-4 h-4" />
                      <span className="font-semibold">Related Requirements</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">
                      This control maps to the following framework requirements:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {currentQuestion.relatedRequirements.map((ref, idx) => (
                        <span 
                          key={idx} 
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500/20 border border-indigo-500/30 rounded-full text-xs text-indigo-300"
                        >
                          <span className="font-medium">{ref.framework}</span>
                          <span className="text-indigo-400">§{ref.reference}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reference */}
                {currentQuestion.referenceText && (
                  <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <FileText className="w-4 h-4" />
                        Reference
                      </div>
                      {currentQuestion.nistUrl && (
                        <a
                          href={currentQuestion.nistUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View NIST/HHS Guidance
                        </a>
                      )}
                    </div>
                    <p className="text-slate-300 text-sm">{currentQuestion.referenceText}</p>
                  </div>
                )}

                {/* Supplemental Info */}
                {currentQuestion.supplementalInfo && (
                  <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-violet-400 text-sm mb-2">
                      <HelpCircle className="w-4 h-4" />
                      Guidance
                    </div>
                    <p className="text-slate-300 text-sm">{currentQuestion.supplementalInfo}</p>
                  </div>
                )}

                {/* Evidence Required - Interactive Checklist with Upload */}
                {currentQuestion.evidenceRequired && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-cyan-400 text-sm">
                        <FolderSearch className="w-4 h-4" />
                        <span className="font-semibold">Evidence Required</span>
                      </div>
                      {Array.isArray(currentQuestion.evidenceRequired) && (
                        <span className="text-xs text-slate-500">
                          {Object.values(currentQuestion.evidenceChecklistStatuses || {}).filter(Boolean).length}/{currentQuestion.evidenceRequired.length} collected
                        </span>
                      )}
                    </div>
                    {Array.isArray(currentQuestion.evidenceRequired) ? (
                      <div className="space-y-2">
                        {currentQuestion.evidenceRequired.map((evidence, idx) => {
                          const isChecked = (currentQuestion.evidenceChecklistStatuses || {})[String(idx)] || false;
                          return (
                            <div key={idx} className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                              isChecked ? 'bg-emerald-500/10 border border-emerald-500/15' : 'bg-slate-800/40 border border-transparent'
                            }`}>
                              <button
                                data-testid={`evidence-check-${idx}`}
                                onClick={() => handleEvidenceChecklistToggle(idx)}
                                className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                  isChecked
                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                    : 'border-slate-500 hover:border-cyan-400'
                                }`}
                              >
                                {isChecked && (
                                  <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                )}
                              </button>
                              <span className={`text-sm flex-1 ${isChecked ? 'text-slate-400 line-through' : 'text-slate-300'}`}>
                                {evidence}
                              </span>
                              <button
                                data-testid={`evidence-upload-${idx}`}
                                onClick={() => { setShowEvidenceModal(true); }}
                                className="flex-shrink-0 text-xs px-2 py-1 rounded bg-slate-700 text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-400 transition-colors"
                                title="Upload evidence"
                              >
                                Upload
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-300 text-sm">{currentQuestion.evidenceRequired}</p>
                    )}
                  </div>
                )}

                {/* Assessment Guidance (for HIPAA) */}
                {currentQuestion.assessmentGuidance && (
                  <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-sky-400 text-sm mb-2">
                      <HelpCircle className="w-4 h-4" />
                      Assessment Guidance
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">{currentQuestion.assessmentGuidance}</p>
                  </div>
                )}

                {/* Trust Services Criteria (for SOC 2) */}
                {currentQuestion.trustServicesCriteria && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-cyan-400 text-sm mb-2">
                      <Shield className="w-4 h-4" />
                      Trust Services Criteria
                    </div>
                    <p className="text-slate-300 text-sm">{currentQuestion.trustServicesCriteria}</p>
                  </div>
                )}

                {/* Testing Procedure (for SOC 2 Type 2) */}
                {currentQuestion.testingProcedure && (
                  <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-teal-400 text-sm mb-2">
                      <ClipboardCheck className="w-4 h-4" />
                      Testing Procedure (Type 2)
                    </div>
                    <p className="text-slate-300 text-sm">{currentQuestion.testingProcedure}</p>
                  </div>
                )}

                {/* Attachments */}
                <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Paperclip className="w-4 h-4" />
                      Attachments ({attachments.length})
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 px-2 py-1 bg-violet-500/10 rounded"
                    >
                      {uploading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3" />
                      )}
                      {uploading ? 'Uploading...' : 'Add File'}
                    </button>
                  </div>
                  {attachments.length === 0 ? (
                    <p className="text-slate-500 text-xs">No attachments. Upload policies, evidence, or documentation.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-2 text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">
                          <FileText className="w-3 h-3" />
                          <span className="max-w-[150px] truncate">{att.fileName}</span>
                          <button
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="text-slate-500 hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reference Documents (from CMS) */}
                {referenceDocuments.length > 0 && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6" data-testid="reference-documents-section">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm mb-3">
                      <FileText className="w-4 h-4" />
                      Reference Documents ({referenceDocuments.length})
                    </div>
                    <p className="text-slate-500 text-xs mb-2">Admin-provided reference materials for this control</p>
                    <div className="space-y-2">
                      {referenceDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg" data-testid={`ref-doc-${doc.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="p-1.5 bg-emerald-500/20 rounded">
                              <FileText className="w-3 h-3 text-emerald-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-white truncate">{doc.fileName}</p>
                              <p className="text-xs text-slate-500">{doc.fileSize < 1024 ? `${doc.fileSize} B` : doc.fileSize < 1048576 ? `${(doc.fileSize / 1024).toFixed(1)} KB` : `${(doc.fileSize / 1048576).toFixed(1)} MB`}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadReferenceDoc(doc)}
                            className="p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded transition-colors"
                            title="Download"
                            data-testid={`download-ref-doc-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Evidence Collection */}
                <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-violet-400 text-sm">
                      <FolderOpen className="w-4 h-4" />
                      Evidence ({questionEvidence.length})
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          fetchEvidenceLibrary();
                          setShowEvidenceModal(true);
                        }}
                        className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 px-2 py-1 bg-violet-500/10 rounded"
                        data-testid="link-evidence-btn"
                      >
                        <LinkIcon className="w-3 h-3" />
                        Link Existing
                      </button>
                      <button
                        onClick={() => evidenceFileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 px-2 py-1 bg-violet-500/10 rounded"
                        data-testid="upload-evidence-btn"
                      >
                        {uploading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        Upload New
                      </button>
                    </div>
                  </div>
                  <input
                    ref={evidenceFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleEvidenceUpload}
                    data-testid="evidence-file-input"
                  />
                  {questionEvidence.length === 0 ? (
                    <p className="text-slate-500 text-xs">No evidence linked. Upload compliance documents or link from your library.</p>
                  ) : (
                    <div className="space-y-2">
                      {questionEvidence.map((ev) => (
                        <div key={ev.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="p-1.5 bg-violet-500/20 rounded">
                              <FileText className="w-3 h-3 text-violet-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-white truncate">{ev.title}</p>
                              <p className="text-xs text-slate-500">{evidenceCategories[ev.category]?.name || ev.category}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <a
                              href={evidenceAPI.getDownloadUrl(ev.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-slate-400 hover:text-violet-400"
                              title="Download"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <button
                              onClick={() => handleUnlinkEvidence(ev.id)}
                              className="p-1 text-slate-400 hover:text-red-400"
                              title="Unlink"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Response Text Box */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 text-slate-300 text-sm font-medium mb-2">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    Response
                  </label>
                  <textarea
                    data-testid="response-input"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Provide your detailed response to this question. Describe how your organization addresses this requirement, including relevant policies, procedures, or controls..."
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent h-32 resize-none"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Describe your implementation details, evidence, or any notes relevant to this control.
                  </p>
                </div>

                {/* Answer Buttons */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <button
                    data-testid="answer-yes"
                    onClick={() => handleAnswer('Y')}
                    disabled={saving}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 relative ${
                      currentQuestion.answer === 'Y'
                        ? 'bg-green-500/20 border-green-500 text-green-400'
                        : 'border-slate-600 text-slate-400 hover:border-green-500/50 hover:bg-green-500/10'
                    }`}
                  >
                    <Check className="w-8 h-8" />
                    <span className="font-semibold">Yes</span>
                    <span className="absolute top-1 right-2 text-xs text-slate-500 opacity-60">1</span>
                  </button>
                  <button
                    data-testid="answer-no"
                    onClick={() => handleAnswer('N')}
                    disabled={saving}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 relative ${
                      currentQuestion.answer === 'N'
                        ? 'bg-red-500/20 border-red-500 text-red-400'
                        : 'border-slate-600 text-slate-400 hover:border-red-500/50 hover:bg-red-500/10'
                    }`}
                  >
                    <X className="w-8 h-8" />
                    <span className="font-semibold">No</span>
                    <span className="absolute top-1 right-2 text-xs text-slate-500 opacity-60">2</span>
                  </button>
                  <button
                    data-testid="answer-na"
                    onClick={() => handleAnswer('NA')}
                    disabled={saving}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 relative ${
                      currentQuestion.answer === 'NA'
                        ? 'bg-gray-500/20 border-gray-500 text-gray-300'
                        : 'border-slate-600 text-slate-400 hover:border-gray-500/50 hover:bg-gray-500/10'
                    }`}
                  >
                    <Minus className="w-8 h-8" />
                    <span className="font-semibold">N/A</span>
                    <span className="absolute top-1 right-2 text-xs text-slate-500 opacity-60">3</span>
                  </button>
                  <button
                    onClick={() => setShowComment(!showComment)}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      showComment || currentQuestion.comment
                        ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                        : 'border-slate-600 text-slate-400 hover:border-purple-500/50 hover:bg-purple-500/10'
                    }`}
                  >
                    <MessageSquare className="w-8 h-8" />
                    <span className="font-semibold">Comment</span>
                  </button>
                </div>

                {/* Level of Effort and Priority Selectors */}
                <div className="flex flex-wrap items-center gap-6 mb-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-400 whitespace-nowrap">Level of Effort:</label>
                    <select
                      value={currentQuestion.levelOfEffort || ''}
                      onChange={(e) => handleEffortPriorityChange('levelOfEffort', e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                        currentQuestion.levelOfEffort === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' :
                        currentQuestion.levelOfEffort === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' :
                        currentQuestion.levelOfEffort === 'High' ? 'bg-red-500/20 text-red-400 border border-red-500/50' :
                        'bg-slate-700 text-slate-300 border border-slate-600'
                      }`}
                      data-testid="level-of-effort-select"
                    >
                      <option value="">Select...</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-400 whitespace-nowrap">Level of Priority:</label>
                    <select
                      value={currentQuestion.levelOfPriority || ''}
                      onChange={(e) => handleEffortPriorityChange('levelOfPriority', e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                        currentQuestion.levelOfPriority === 'Low' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' :
                        currentQuestion.levelOfPriority === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' :
                        currentQuestion.levelOfPriority === 'High' ? 'bg-red-500/20 text-red-400 border border-red-500/50' :
                        'bg-slate-700 text-slate-300 border border-slate-600'
                      }`}
                      data-testid="level-of-priority-select"
                    >
                      <option value="">Select...</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                </div>

                {/* Comment Box */}
                {showComment && (
                  <div className="mb-6">
                    <textarea
                      data-testid="comment-input"
                      value={comment || currentQuestion.comment || ''}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add your comments or notes here..."
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none"
                    />
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                  <button
                    data-testid="prev-question"
                    onClick={() => {
                      if (currentIndex > 0) {
                        setCurrentIndex(currentIndex - 1);
                        setComment('');
                        setResponse('');
                      }
                    }}
                    disabled={currentIndex === 0}
                    className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span>Previous</span>
                    <span className="text-xs text-slate-600 ml-1">←</span>
                  </button>
                  <div className="flex items-center gap-3">
                    {saving ? (
                      <div className="flex items-center gap-2 text-blue-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">
                        Question {currentIndex + 1} of {filteredQuestions.length}
                      </span>
                    )}
                  </div>
                  <button
                    data-testid="next-question"
                    onClick={() => {
                      if (currentIndex < filteredQuestions.length - 1) {
                        setCurrentIndex(currentIndex + 1);
                        setComment('');
                        setResponse('');
                      }
                    }}
                    disabled={currentIndex === filteredQuestions.length - 1}
                    className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>Next</span>
                    <span className="text-xs text-slate-600 mr-1">→</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Keyboard shortcuts hint */}
                <div className="text-center pt-3 border-t border-slate-700/50 mt-4">
                  <p className="text-xs text-slate-500">
                    <span className="text-slate-400">Keyboard shortcuts:</span>{' '}
                    <span className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 mx-1">1</span> Yes{' '}
                    <span className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 mx-1">2</span> No{' '}
                    <span className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 mx-1">3</span> N/A{' '}
                    <span className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 mx-1">← →</span> Navigate
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Library Modal */}
      {showEvidenceModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Link Evidence from Library</h3>
              <button
                onClick={() => setShowEvidenceModal(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {evidenceLibrary.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No evidence in library yet</p>
                  <p className="text-sm text-slate-500">Upload evidence using the "Upload New" button</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {evidenceLibrary.map((ev) => {
                    const isLinked = questionEvidence.some(qe => qe.id === ev.id);
                    return (
                      <div
                        key={ev.id}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          isLinked ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-slate-700/50 hover:bg-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2 bg-violet-500/20 rounded">
                            <FileText className="w-4 h-4 text-violet-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{ev.title}</p>
                            <p className="text-xs text-slate-400">{ev.fileName}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleLinkEvidence(ev.id)}
                          disabled={isLinked}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            isLinked
                              ? 'bg-violet-500/20 text-violet-400 cursor-default'
                              : 'bg-violet-600 hover:bg-violet-700 text-white'
                          }`}
                        >
                          {isLinked ? 'Linked' : 'Link'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assessment;
